import { type ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { OverlayTriggerStateContext } from 'react-aria-components';

import { Button } from '@/components/react-aria/button';
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from '@/components/react-aria/dialog';
import { Modal } from '@/components/react-aria/modal';
import { dispatchShowAlert } from '@/renderer/utils/alerts';
import { request } from '@/renderer/utils/api';
import type { RequestSchema } from '@/types/request';

const USERS_ENDPOINT = '/api/v1/admin/search/users/or';
const PRODUCTS_ENDPOINT = '/api/v1.1/admin/search/membershipProduct';
const CREATE_ENDPOINT = '/api/v1/admin/order/create';

// Payment methods for the manual-order dropdown. `method` is stored as free text
// on the backend, so this list is purely the admin-facing set of options.
export const METHOD_OPTIONS = [
  'Stripe',
  'Credit',
  'PayPal',
  'Venmo',
  'Cash App',
  'Zelle',
  'Cash',
  'Bitcoin',
  'ETH',
  'Cryptocurrency',
  'Balance',
  'Comp',
  'Free',
];

// Marketing-attribution fields written to the order's utm_*/referrer/landing
// columns. Prefilled from a crypto buyer's captured checkout intent, but always
// editable so an admin can correct or clear them. Keys match the order columns
// and the shared ATTRIBUTION_KEYS on the server.
const ATTRIBUTION_FIELDS: { key: string; label: string; placeholder?: string }[] = [
  { key: 'utmSource', label: 'Source', placeholder: 'e.g. substack' },
  { key: 'utmMedium', label: 'Medium', placeholder: 'e.g. email' },
  { key: 'utmCampaign', label: 'Campaign' },
  { key: 'utmContent', label: 'Content' },
  { key: 'utmTerm', label: 'Term' },
  { key: 'referrer', label: 'Referrer' },
  { key: 'landingPage', label: 'Landing page' },
  { key: 'twclid', label: 'Twitter click ID' },
];

type UserResult = {
  id: number;
  firstName?: string;
  lastName?: string;
  username?: string;
  email?: string;
};
type OfferResult = {
  price?: number;
  plays?: number;
  bonus?: number;
  ids?: number[];
  ui?: { title?: string; prorated?: string };
};
type ProductResult = { id: number; title?: string; offers?: OfferResult[] };

// A promotional credit ('credit') or discount ('discount') grant the selected
// user can apply to this manual order, as returned by /admin/user/:id/order-credit.
type CreditGrant = {
  id: number;
  remaining?: number;
  amount?: number;
  discountPct?: number | null;
  discountAmount?: number | null;
  expiresAt?: string | null;
  campaign?: string | null;
  description?: string | null;
};
type CreditInfo = { balance: number; credits: CreditGrant[]; discounts: CreditGrant[] };

// Round to cents, mirroring the server's round2 so the shown cash-due matches what
// the server records (the server re-clamps and is authoritative).
function round2(n: number) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// Dollar value of a discount grant against a price — a fraction of it (discountPct)
// or a fixed ceiling (discountAmount), never more than the price. Mirrors the
// server's discountValue.
function discountValueFor(grant: CreditGrant, base: number) {
  const b = Math.max(0, base);
  if (grant.discountPct != null) {
    return round2(Math.min(b, b * Math.min(1, Math.max(0, grant.discountPct))));
  }
  if (grant.discountAmount != null) {
    return round2(Math.min(b, Math.max(0, grant.discountAmount)));
  }
  return 0;
}

export function CreateOrderDialog({
  onSuccess,
  initialUser,
  // Let callers restyle/relabel the trigger button so the dialog can live in
  // other places (e.g. the support-chat header) and match that UI.
  triggerClassName = 'inline-flex h-9 items-center gap-1.5 rounded-lg bg-zinc-900 px-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-zinc-700 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100',
  triggerChildren = 'Create Order',
  // Offset the overlay past the fixed app sidebar (md:w-72) by default so the
  // dialog centers within the content area.
  modalOverlayClassName = 'md:left-72 md:w-[calc(100%-18rem)]',
}: {
  onSuccess: () => void;
  /** Preselect a user (skips the search step) — e.g. the viewed support-chat user. */
  initialUser?: UserResult;
  triggerClassName?: string;
  triggerChildren?: ReactNode;
  modalOverlayClassName?: string;
}) {
  // Self-contained react-aria DialogTrigger — the trigger provides the overlay
  // state context the dialog's close controls rely on, so open/close work the
  // same everywhere this is used.
  return (
    <DialogTrigger>
      <Button variant="unstyle" className={triggerClassName}>
        {triggerChildren}
      </Button>
      <Modal size="lg" classNames={{ modalOverlay: modalOverlayClassName }}>
        <Dialog>
          <CreateOrderForm initialUser={initialUser} onSuccess={onSuccess} />
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}

// Exported for unit tests — the dialog itself wraps this in the react-aria
// DialogTrigger/Modal that provides the overlay-state context.
export function CreateOrderForm({
  onSuccess,
  initialUser,
}: {
  onSuccess: () => void;
  initialUser?: UserResult;
}) {
  const dialogState = useContext(OverlayTriggerStateContext);
  const [submitting, setSubmitting] = useState(false);

  const initialUserLabel = initialUser
    ? [initialUser.firstName, initialUser.lastName].filter(Boolean).join(' ') ||
      initialUser.username ||
      String(initialUser.id)
    : '';

  // User search (seeded from initialUser when preselected)
  const [userQuery, setUserQuery] = useState(initialUserLabel);
  const [userResults, setUserResults] = useState<UserResult[]>([]);
  const [userSearching, setUserSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserResult | null>(initialUser ?? null);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDebounce = useRef<ReturnType<typeof setTimeout>>();

  // Product search
  const [productQuery, setProductQuery] = useState('');
  const [productResults, setProductResults] = useState<ProductResult[]>([]);
  const [productSearching, setProductSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductResult | null>(null);
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);
  const productDebounce = useRef<ReturnType<typeof setTimeout>>();

  // Selected offer (from product.offers)
  const [selectedOfferIdx, setSelectedOfferIdx] = useState(0);
  const offers: OfferResult[] = selectedProduct?.offers ?? [];
  const selectedOffer = offers[selectedOfferIdx] ?? null;

  function applyOffer(offer: OfferResult | null) {
    if (!offer) {
      return;
    }
    if (offer.price != null) {
      setOfferPrice(String(offer.price));
      // Auto-fill the charge from the offer's face price — EXCEPT on a free/comp
      // order, where the charge is locked at $0 and selecting/changing the offer
      // must not overwrite it back to the full price. (The offer price itself is
      // still recorded so the membership is created for the right product/offer.)
      if (!free) {
        setAmount(String(offer.price));
      }
    }
    // Season passes carry a coverage-toggle sentinel here (8888/9999); the
    // server's calculatePrice translates it to the real issued count via the
    // offer's estimatedPlays. Seed the offer's raw value and let the server
    // (the single source of truth) do the translation — see the offerId sent on
    // submit, which is what makes the server resolve the correct offer.
    if (offer.plays != null) {
      setRemainingPlays(String(offer.plays));
    }
    if (offer.bonus != null) {
      setBalanceUsed(''); // bonus is separate; leave balance used alone
    }
  }

  // Form fields
  const [amount, setAmount] = useState('');
  const [offerPrice, setOfferPrice] = useState('');
  const [remainingPlays, setRemainingPlays] = useState('');
  const [balanceUsed, setBalanceUsed] = useState('');
  const [discount, setDiscount] = useState('');

  // Free / comp order. When on, the charge is locked at $0 and selecting or
  // changing the service/offer will NOT auto-fill the amount back to the offer
  // price — the membership is still created for the chosen product/offer, the
  // cash is just $0 and the order records as a comp. Toggling it back off
  // restores the normal auto-fill from the selected offer's price.
  const [free, setFree] = useState(false);
  // Remember the method the admin had before flipping Free on, so turning Free
  // off restores it instead of leaving the auto-set comp method behind.
  const methodBeforeFree = useRef('');

  // Account credit the selected user can apply to this manual order. Fetched when
  // a user is selected; drives the "Apply account credit" panel and the computed
  // cash due (offer price − discount − promo credit − balance).
  const [creditInfo, setCreditInfo] = useState<CreditInfo | null>(null);
  const [applyBalanceAmt, setApplyBalanceAmt] = useState('');
  const [applyCreditAmt, setApplyCreditAmt] = useState('');
  const [applyDiscountId, setApplyDiscountId] = useState('');
  const [method, setMethod] = useState('');
  const [payerEmail, setPayerEmail] = useState('');
  const [payerName, setPayerName] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [createMembership, setCreateMembership] = useState(true);
  const [sendEmail, setSendEmail] = useState(true);

  // Marketing attribution (utm_*/referrer/landing). Prefilled from the user's
  // captured crypto-checkout intent when the dialog opens for a specific user,
  // but fully editable so the admin can correct or clear it. Empty (not error)
  // when nothing was captured.
  const [attribution, setAttribution] = useState<Record<string, string>>({});
  const [attributionCapturedAt, setAttributionCapturedAt] = useState<string | null>(null);
  const [attributionProduct, setAttributionProduct] = useState<string | null>(null);
  const setAttributionField = (key: string, value: string) =>
    setAttribution((prev) => ({ ...prev, [key]: value }));
  const hasAttribution = ATTRIBUTION_FIELDS.some(({ key }) => attribution[key]?.trim());

  // Fetch the captured crypto-order attribution for the preselected user (e.g.
  // when launched from their support chat) and prefill the fields. Fail-soft:
  // a missing intent just leaves the fields empty.
  useEffect(() => {
    const id = initialUser?.id;
    if (!id) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await request(`/api/v1/admin/user/${id}/crypto-intent` as any, {});
        const intent = (res as any)?.data;
        if (cancelled || !intent || typeof intent !== 'object') {
          return;
        }
        const next: Record<string, string> = {};
        for (const { key } of ATTRIBUTION_FIELDS) {
          if (typeof intent[key] === 'string' && intent[key]) {
            next[key] = intent[key];
          }
        }
        setAttribution(next);
        setAttributionCapturedAt(typeof intent.at === 'string' ? intent.at : null);
        setAttributionProduct(typeof intent.productTitle === 'string' ? intent.productTitle : null);
      } catch {
        // No captured attribution / not reachable — leave the fields empty.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialUser?.id]);

  // Fetch the selected user's applicable balance / promo credit / discount grants
  // for the "Apply account credit" panel. Resets the apply inputs on user change.
  // Fail-soft: unreachable or empty just hides the panel.
  useEffect(() => {
    const id = selectedUser?.id;
    setApplyBalanceAmt('');
    setApplyCreditAmt('');
    setApplyDiscountId('');
    if (!id) {
      setCreditInfo(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await request(`/api/v1/admin/user/${id}/order-credit` as any, {});
        const info = (res as any)?.data;
        if (!cancelled && info && typeof info === 'object') {
          setCreditInfo({
            balance: Number(info.balance) || 0,
            credits: Array.isArray(info.credits) ? info.credits : [],
            discounts: Array.isArray(info.discounts) ? info.discounts : [],
          });
        }
      } catch {
        if (!cancelled) {
          setCreditInfo(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedUser?.id]);

  // Autofill email/name when user is selected
  useEffect(() => {
    if (selectedUser) {
      const fullName = [selectedUser.firstName, selectedUser.lastName].filter(Boolean).join(' ');
      if (!payerEmail && selectedUser.email) {
        setPayerEmail(selectedUser.email);
      }
      if (!payerName && fullName) {
        setPayerName(fullName);
      }
    }
  }, [selectedUser]);

  // Search users with debounce
  function onUserQueryChange(q: string) {
    setUserQuery(q);
    setSelectedUser(null);
    setUserDropdownOpen(true);
    clearTimeout(userDebounce.current);
    if (!q.trim()) {
      setUserResults([]);
      return;
    }
    userDebounce.current = setTimeout(async () => {
      setUserSearching(true);
      try {
        const res = await request(USERS_ENDPOINT as any, {
          query: {
            data: { username: q, firstName: q, lastName: q, email: q },
            page: { limit: 8 },
          },
        });
        setUserResults((res as any)?.data ?? []);
      } catch {
        setUserResults([]);
      } finally {
        setUserSearching(false);
      }
    }, 300);
  }

  function selectUser(u: UserResult) {
    setSelectedUser(u);
    setUserQuery([u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || String(u.id));
    setUserDropdownOpen(false);
    setUserResults([]);
  }

  // Search products with debounce; empty query loads recent products
  function fetchProducts(q: string) {
    clearTimeout(productDebounce.current);
    productDebounce.current = setTimeout(
      async () => {
        setProductSearching(true);
        try {
          const res = await request(PRODUCTS_ENDPOINT as any, {
            query: {
              data: q.trim() ? { title: q } : {},
              order: { by: 'createdAt', dir: 'DESC' },
              page: { limit: 5 },
            },
          });
          setProductResults((res as any)?.data ?? []);
        } catch {
          setProductResults([]);
        } finally {
          setProductSearching(false);
        }
      },
      q.trim() ? 300 : 0,
    );
  }

  function onProductQueryChange(q: string) {
    setProductQuery(q);
    setSelectedProduct(null);
    setProductDropdownOpen(true);
    fetchProducts(q);
  }

  function onProductFocus() {
    setProductDropdownOpen(true);
    if (!productResults.length && !productSearching) {
      fetchProducts(productQuery);
    }
  }

  function selectProduct(p: ProductResult) {
    setSelectedProduct(p);
    setProductQuery(p.title ?? String(p.id));
    setProductDropdownOpen(false);
    setProductResults([]);
    setSelectedOfferIdx(0);
    applyOffer(p.offers?.[0] ?? null);
  }

  // Re-apply offer values whenever the selected offer changes
  useEffect(() => {
    applyOffer(selectedOffer);
  }, [selectedOfferIdx, selectedProduct]);

  // --- Applied-credit math (mirrors the server's planManualCredits) ---
  // Discount comes off the price first, then promo credit, then balance; each is
  // clamped to what is available and to the price still owed. The server re-clamps
  // and is authoritative — this only drives the panel and the shown cash due.
  const offerPriceNum = parseFloat(offerPrice) || 0;
  const balanceAvail = round2(creditInfo?.balance ?? 0);
  const spendableCredit = round2(
    (creditInfo?.credits ?? []).reduce((sum, g) => sum + (Number(g.remaining) || 0), 0),
  );
  const hasCreditPanel =
    !free &&
    !!creditInfo &&
    (balanceAvail > 0 || spendableCredit > 0 || (creditInfo.discounts?.length ?? 0) > 0);

  const selectedDiscount =
    (creditInfo?.discounts ?? []).find((d) => String(d.id) === applyDiscountId) || null;
  const discountDollars = selectedDiscount ? discountValueFor(selectedDiscount, offerPriceNum) : 0;
  const afterDiscount = round2(Math.max(0, offerPriceNum - discountDollars));
  const creditApplied = round2(
    Math.max(0, Math.min(parseFloat(applyCreditAmt) || 0, spendableCredit, afterDiscount)),
  );
  const balanceApplied = round2(
    Math.max(
      0,
      Math.min(parseFloat(applyBalanceAmt) || 0, balanceAvail, afterDiscount - creditApplied),
    ),
  );
  const cashDue = round2(afterDiscount - creditApplied - balanceApplied);
  const manualCreditsActive =
    hasCreditPanel && (creditApplied > 0 || balanceApplied > 0 || discountDollars > 0);

  // Keep the "Amount Paid" field in sync with the computed cash due while credit
  // is being applied (the server records this value, so the two must agree).
  useEffect(() => {
    if (manualCreditsActive) {
      setAmount(cashDue.toFixed(2));
    }
  }, [manualCreditsActive, cashDue]);

  // Flip the Free / comp toggle. On → lock the charge at $0, clear any legacy
  // balance-used, and record a sensible comp payment method ('Comp'). Off →
  // restore the admin's prior method and re-apply the selected offer's price.
  function onFreeChange(next: boolean) {
    setFree(next);
    if (next) {
      methodBeforeFree.current = method;
      setMethod('Comp');
      setAmount('0');
      setBalanceUsed('');
    } else {
      setMethod(methodBeforeFree.current);
      setAmount(selectedOffer?.price != null ? String(selectedOffer.price) : '');
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedUser) {
      dispatchShowAlert({ message: 'Please select a user', status: 'error' });
      return;
    }
    if (!selectedProduct) {
      dispatchShowAlert({ message: 'Please select a product', status: 'error' });
      return;
    }
    // A free/comp order is locked at $0, so the amount is not a required input
    // (it is forced to 0 below).
    if (!free && !amount) {
      dispatchShowAlert({ message: 'Amount is required', status: 'error' });
      return;
    }
    // The list price is only used (and shown) when applying account credit, where
    // it's the base the discount/credit/balance is subtracted from.
    if (hasCreditPanel && !offerPrice) {
      dispatchShowAlert({ message: 'Offer price is required', status: 'error' });
      return;
    }
    if (!remainingPlays) {
      dispatchShowAlert({ message: 'Remaining plays is required', status: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      await request(CREATE_ENDPOINT as unknown as RequestSchema<any, any>, {
        method: 'POST',
        body: {
          data: {
            userId: selectedUser.id,
            productId: selectedProduct.id,
            // Pass the selected offer index so the server merges the correct base
            // offer (its ui.title, type and estimatedPlays). Without it the server
            // defaults to offer 0, mislabeling the order and pricing/issuing plays
            // off the wrong offer.
            offerId: selectedOfferIdx,
            // A free/comp order is always $0 regardless of the offer price. Otherwise
            // when credit is applied the server recomputes cash from the offer price
            // and the applied credit/discount; send the shown cash due so the two
            // agree even if the server re-clamps.
            amount: free ? 0 : manualCreditsActive ? cashDue : parseFloat(amount),
            // Record a comp method for a free order so the $0 order reads cleanly in
            // the orders view and reporting; otherwise use the admin's chosen method.
            method: free ? 'Comp' : method || undefined,
            payerEmail: payerEmail || undefined,
            payerName: payerName || undefined,
            transactionId: transactionId || undefined,
            createMembership,
            doSendEmail: sendEmail,
            // Marketing attribution — written to the order's utm_*/referrer/
            // landingPage columns (server routes it through the same flag-gated
            // writer the card checkout uses). Only send non-empty fields.
            ...Object.fromEntries(
              ATTRIBUTION_FIELDS.map(({ key }) => [key, attribution[key]?.trim()]).filter(
                ([, v]) => v,
              ),
            ),
            offer: {
              // The server records the order's amount from the OFFER price
              // (calculatePrice → order.amount), NOT the payment `amount` above. So
              // the charge the admin actually wants recorded has to ride in on
              // offer.price:
              //   • free/comp        → 0
              //   • applying credit  → the offer's list price, which the credit
              //     panel's discount/credit/balance math subtracts from (the server
              //     records the resulting cash due).
              //   • otherwise        → the Amount Paid field, so a custom amount is
              //     recorded as entered (previously ignored — the list price was
              //     always recorded instead).
              price: free ? 0 : manualCreditsActive ? parseFloat(offerPrice) : parseFloat(amount),
              plays: parseInt(remainingPlays, 10),
              // The legacy free-text "Balance Used" is only used when the credit
              // panel is not driving the split — otherwise the server draws the
              // real pools from `credits` below and computes balanceUsed itself.
              balanceUsed: manualCreditsActive ? 0 : balanceUsed ? parseFloat(balanceUsed) : 0,
              ids: selectedOffer?.ids ?? [],
            },
            // Staff-chosen credit application: dollars of the user's refundable
            // balance and promo credit to draw, plus an optional discount grant.
            // The server clamps each to availability and to the price.
            ...(manualCreditsActive
              ? {
                  credits: {
                    balance: balanceApplied,
                    credit: creditApplied,
                    discountGrantId: selectedDiscount ? selectedDiscount.id : undefined,
                  },
                }
              : {}),
          },
        },
      });
      dialogState?.close();
      dispatchShowAlert({ message: 'Order created', status: 'info' });
      onSuccess();
    } catch (err: any) {
      dispatchShowAlert({ message: err?.message || 'Failed to create order', status: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <DialogHeader>Create Manual Order</DialogHeader>
      <DialogCloseButton />
      <form onSubmit={handleSubmit}>
        <DialogBody className="space-y-5">
          {/* Free / comp toggle — a fast $0 comp path. When on, the charge locks
              at $0 and the offer's price no longer auto-fills the amount, so an
              admin can comp a membership without editing the amount back to $0
              every time. Mirrors the release-center Free/Charge toggle. */}
          <div className="flex items-center justify-between rounded-lg border border-zinc-950/10 px-3 py-2.5 dark:border-white/10">
            <div>
              <div className="text-sm font-medium text-zinc-900 dark:text-white">
                Free / comp order
              </div>
              <div className="text-xs text-zinc-500 dark:text-zinc-400">
                Locks the charge at $0 and records it as a comp
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-zinc-700 dark:text-zinc-300">Free</span>
              <label
                htmlFor="free-comp-toggle"
                className="relative inline-flex cursor-pointer items-center"
              >
                <input
                  id="free-comp-toggle"
                  type="checkbox"
                  checked={free}
                  onChange={(e) => onFreeChange(e.target.checked)}
                  className="peer sr-only"
                />
                {/* Track */}
                <span
                  className="block h-6 w-11 rounded-full bg-zinc-200 transition peer-checked:bg-emerald-600 dark:bg-zinc-700"
                  aria-hidden="true"
                />
                {/* Thumb */}
                <span
                  className="absolute top-[2px] left-[2px] h-5 w-5 rounded-full border border-zinc-300 bg-white transition-transform peer-checked:translate-x-5 peer-checked:border-white"
                  aria-hidden="true"
                />
              </label>
            </div>
          </div>

          {/* Row 1: Amount, Discount, Method */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field
              label={
                free
                  ? '$ Amount Paid (free / comp)'
                  : manualCreditsActive
                    ? '$ Cash Due (computed)'
                    : '$ Amount Paid'
              }
              required
            >
              <div className="flex">
                <span className="inline-flex items-center rounded-l-md border border-r-0 border-zinc-950/10 bg-zinc-50 px-2.5 text-xs text-zinc-500 dark:border-white/10 dark:bg-zinc-800 dark:text-zinc-400">
                  US$
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={free ? '0' : amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className={
                    inputCls +
                    ' rounded-l-none' +
                    (free || manualCreditsActive ? ' bg-zinc-50 dark:bg-zinc-800' : '')
                  }
                  readOnly={free || manualCreditsActive}
                  title={
                    free
                      ? 'Free / comp order — the charge is locked at $0'
                      : manualCreditsActive
                        ? 'Computed from the offer price minus the applied discount, credit and balance'
                        : undefined
                  }
                  required
                />
              </div>
            </Field>
            <Field label="Discount %">
              <div className="flex">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  placeholder="0"
                  className={inputCls + ' rounded-r-none'}
                />
                <span className="inline-flex items-center rounded-r-md border border-l-0 border-zinc-950/10 bg-zinc-50 px-2.5 text-xs text-zinc-500 dark:border-white/10 dark:bg-zinc-800 dark:text-zinc-400">
                  %
                </span>
              </div>
            </Field>
            <Field label="Payment Method">
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className={inputCls + (free ? ' bg-zinc-50 dark:bg-zinc-800' : '')}
                disabled={free}
                title={free ? 'Free / comp order — recorded as a comp' : undefined}
              >
                <option value="">— None —</option>
                {METHOD_OPTIONS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {/* Row 2: Email, Name, Transaction ID */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Email">
              <input
                type="email"
                value={payerEmail}
                onChange={(e) => setPayerEmail(e.target.value)}
                placeholder="Autofill from user"
                className={inputCls}
              />
            </Field>
            <Field label="Name">
              <input
                type="text"
                value={payerName}
                onChange={(e) => setPayerName(e.target.value)}
                placeholder="Autofill from user"
                className={inputCls}
              />
            </Field>
            <Field label="Transaction ID">
              <input
                type="text"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                placeholder="From payment method"
                className={inputCls}
              />
            </Field>
          </div>

          {/* Row 3: Checkboxes */}
          <div className="flex gap-6">
            <CheckboxField
              label="Create Membership?"
              checked={createMembership}
              onChange={setCreateMembership}
              hint="Also creates a Membership record"
            />
            <CheckboxField
              label="Send Email?"
              checked={sendEmail}
              onChange={setSendEmail}
              hint="Sends confirmation to admin and user"
            />
          </div>

          {/* User search */}
          <Field label="User" required>
            <div className="relative">
              <input
                type="text"
                value={userQuery}
                onChange={(e) => onUserQueryChange(e.target.value)}
                onFocus={() => userQuery && setUserDropdownOpen(true)}
                onBlur={() => setTimeout(() => setUserDropdownOpen(false), 150)}
                placeholder="Search by name, username or email…"
                className={inputCls}
                autoComplete="off"
              />
              {userDropdownOpen && (userSearching || userResults.length > 0) && (
                <div className="absolute bottom-full z-50 mb-1 max-h-48 w-full overflow-y-auto rounded-lg border border-zinc-950/10 bg-white shadow-lg dark:border-white/10 dark:bg-zinc-900">
                  {userSearching ? (
                    <div className="px-3 py-2 text-xs text-zinc-400">Searching…</div>
                  ) : (
                    userResults.map((u) => {
                      const name =
                        [u.firstName, u.lastName].filter(Boolean).join(' ') ||
                        u.username ||
                        String(u.id);
                      return (
                        <button
                          key={u.id}
                          type="button"
                          onMouseDown={() => selectUser(u)}
                          className="flex w-full flex-col px-3 py-2 text-left hover:bg-zinc-50 dark:hover:bg-zinc-800"
                        >
                          <span className="text-sm font-medium text-zinc-900 dark:text-white">
                            {name}
                          </span>
                          {u.email && (
                            <span className="text-xs text-zinc-500 dark:text-zinc-400">
                              {u.email}
                            </span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </Field>

          {/* Product search */}
          <Field label="Product" required>
            <div className="relative">
              <input
                type="text"
                value={productQuery}
                onChange={(e) => onProductQueryChange(e.target.value)}
                onFocus={onProductFocus}
                onBlur={() => setTimeout(() => setProductDropdownOpen(false), 150)}
                placeholder="Search by product title…"
                className={inputCls}
                autoComplete="off"
              />
              {productDropdownOpen &&
                (productSearching || productResults.length > 0 || productQuery === '') && (
                  <div className="absolute bottom-full z-50 mb-1 max-h-48 w-full overflow-y-auto rounded-lg border border-zinc-950/10 bg-white shadow-lg dark:border-white/10 dark:bg-zinc-900">
                    {productSearching ? (
                      <div className="px-3 py-2 text-xs text-zinc-400">Searching…</div>
                    ) : (
                      productResults.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onMouseDown={() => selectProduct(p)}
                          className="flex w-full px-3 py-2 text-left hover:bg-zinc-50 dark:hover:bg-zinc-800"
                        >
                          <span className="text-sm font-medium text-zinc-900 dark:text-white">
                            {p.title ?? `Product ${p.id}`}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
            </div>
          </Field>

          {/* Offer selector — only shown when selected product has multiple offers */}
          {offers.length > 1 && (
            <Field label="Offer">
              <select
                value={selectedOfferIdx}
                onChange={(e) => setSelectedOfferIdx(Number(e.target.value))}
                className={inputCls}
              >
                {offers.map((o, idx) => {
                  const title = o.ui?.title || `Offer ${idx + 1}`;
                  const price = o.price != null ? ` — $${o.price.toFixed(2)}` : '';
                  const plays = o.plays != null ? ` (${o.plays} plays)` : '';
                  return (
                    <option key={idx} value={idx}>
                      {title}
                      {price}
                      {plays}
                    </option>
                  );
                })}
              </select>
            </Field>
          )}

          {/* Row 4: Offer Price (list, for credit math only), Remaining Plays,
              Balance Used. The charge itself is the "Amount Paid" field above —
              offer.price is sent from it — so the list-price field is only shown
              when the credit panel needs a base to subtract discounts/credit from.
              A free/comp order and a plain custom-amount order don't show it. */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {hasCreditPanel && (
              <Field label="$ Offer Price (list)" required>
                <div className="flex">
                  <span className="inline-flex items-center rounded-l-md border border-r-0 border-zinc-950/10 bg-zinc-50 px-2.5 text-xs text-zinc-500 dark:border-white/10 dark:bg-zinc-800 dark:text-zinc-400">
                    US$
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={offerPrice}
                    onChange={(e) => setOfferPrice(e.target.value)}
                    placeholder="0.00"
                    className={inputCls + ' rounded-l-none'}
                    required
                  />
                </div>
              </Field>
            )}
            <Field label="Remaining Plays" required>
              <input
                type="number"
                step="1"
                min="0"
                value={remainingPlays}
                onChange={(e) => setRemainingPlays(e.target.value)}
                placeholder="0"
                className={inputCls}
                required
              />
            </Field>
            {/* Legacy free-text balance field — hidden once the user has real
                balance/credit/discount to apply, which the panel below handles,
                and on a free/comp order (nothing to draw against a $0 charge). */}
            {!hasCreditPanel && !free && (
              <Field label="Balance Used">
                <div className="flex">
                  <span className="inline-flex items-center rounded-l-md border border-r-0 border-zinc-950/10 bg-zinc-50 px-2.5 text-xs text-zinc-500 dark:border-white/10 dark:bg-zinc-800 dark:text-zinc-400">
                    US$
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={balanceUsed}
                    onChange={(e) => setBalanceUsed(e.target.value)}
                    placeholder="0.00"
                    className={inputCls + ' rounded-l-none'}
                  />
                </div>
              </Field>
            )}
          </div>

          {/* Apply account credit — the selected user's refundable balance, promo
              credit grants and one-shot discounts. Staff choose how much of each to
              apply; the cash due is computed and drives the Amount Paid field. */}
          {hasCreditPanel && (
            <div className="space-y-3 rounded-lg border border-zinc-950/10 p-3 dark:border-white/10">
              <div className="flex flex-wrap items-baseline justify-between gap-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                  Apply account credit
                </span>
                <span className="text-[11px] text-zinc-400 dark:text-zinc-500">
                  Draws from the user's balance, promo credit and discounts
                </span>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label={`Apply balance (avail $${balanceAvail.toFixed(2)})`}>
                  <div className="flex">
                    <span className="inline-flex items-center rounded-l-md border border-r-0 border-zinc-950/10 bg-zinc-50 px-2.5 text-xs text-zinc-500 dark:border-white/10 dark:bg-zinc-800 dark:text-zinc-400">
                      US$
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max={balanceAvail}
                      value={applyBalanceAmt}
                      onChange={(e) => setApplyBalanceAmt(e.target.value)}
                      placeholder="0.00"
                      className={inputCls + ' rounded-l-none'}
                      disabled={balanceAvail <= 0}
                    />
                  </div>
                </Field>
                <Field label={`Apply promo credit (avail $${spendableCredit.toFixed(2)})`}>
                  <div className="flex">
                    <span className="inline-flex items-center rounded-l-md border border-r-0 border-zinc-950/10 bg-zinc-50 px-2.5 text-xs text-zinc-500 dark:border-white/10 dark:bg-zinc-800 dark:text-zinc-400">
                      US$
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max={spendableCredit}
                      value={applyCreditAmt}
                      onChange={(e) => setApplyCreditAmt(e.target.value)}
                      placeholder="0.00"
                      className={inputCls + ' rounded-l-none'}
                      disabled={spendableCredit <= 0}
                    />
                  </div>
                </Field>
              </div>

              {creditInfo.credits.length > 0 && (
                <ul className="space-y-0.5 text-[11px] text-zinc-500 dark:text-zinc-400">
                  {creditInfo.credits.map((g) => (
                    <li key={g.id}>
                      Credit #{g.id}: ${Number(g.remaining ?? 0).toFixed(2)} left
                      {g.campaign ? ` · ${g.campaign}` : ''}
                      {g.expiresAt ? ` · exp ${new Date(g.expiresAt).toLocaleDateString()}` : ''}
                    </li>
                  ))}
                </ul>
              )}

              {creditInfo.discounts.length > 0 && (
                <Field label="Apply discount">
                  <select
                    value={applyDiscountId}
                    onChange={(e) => setApplyDiscountId(e.target.value)}
                    className={inputCls}
                  >
                    <option value="">— None —</option>
                    {creditInfo.discounts.map((d) => {
                      const label =
                        d.discountPct != null
                          ? `${Math.round(d.discountPct * 100)}% off`
                          : `$${Number(d.discountAmount ?? 0).toFixed(2)} off`;
                      return (
                        <option key={d.id} value={String(d.id)}>
                          #{d.id} · {label}
                          {d.campaign ? ` · ${d.campaign}` : ''}
                          {d.expiresAt
                            ? ` · exp ${new Date(d.expiresAt).toLocaleDateString()}`
                            : ''}
                        </option>
                      );
                    })}
                  </select>
                </Field>
              )}

              {/* Split summary: price − discount − credit − balance = cash due */}
              <div className="rounded-md bg-zinc-50 p-2.5 text-xs text-zinc-700 dark:bg-zinc-800/50 dark:text-zinc-300">
                <div className="flex justify-between">
                  <span>Offer price</span>
                  <span>${offerPriceNum.toFixed(2)}</span>
                </div>
                {discountDollars > 0 && (
                  <div className="flex justify-between text-emerald-700 dark:text-emerald-300">
                    <span>− Discount</span>
                    <span>−${discountDollars.toFixed(2)}</span>
                  </div>
                )}
                {creditApplied > 0 && (
                  <div className="flex justify-between">
                    <span>− Promo credit</span>
                    <span>−${creditApplied.toFixed(2)}</span>
                  </div>
                )}
                {balanceApplied > 0 && (
                  <div className="flex justify-between">
                    <span>− Balance</span>
                    <span>−${balanceApplied.toFixed(2)}</span>
                  </div>
                )}
                <div className="mt-1 flex justify-between border-t border-zinc-950/10 pt-1 font-semibold text-zinc-900 dark:border-white/10 dark:text-white">
                  <span>Cash due</span>
                  <span>${cashDue.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Marketing attribution — prefilled from a crypto buyer's captured
              checkout intent, editable, written to the order's utm, referrer
              and landing columns so crypto orders attribute like card orders. */}
          <details
            className="rounded-md border border-zinc-950/10 dark:border-white/10"
            open={hasAttribution}
          >
            <summary className="flex cursor-pointer list-none items-center justify-between px-3 py-2 text-xs font-medium text-zinc-700 dark:text-zinc-300">
              <span>Marketing attribution</span>
              {attributionCapturedAt ? (
                <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 font-normal text-[10px] text-emerald-800 dark:bg-emerald-400/20 dark:text-emerald-200">
                  From crypto checkout
                </span>
              ) : (
                <span className="ml-2 font-normal text-[10px] text-zinc-400 dark:text-zinc-500">
                  Optional
                </span>
              )}
            </summary>
            <div className="space-y-3 border-t border-zinc-950/10 px-3 py-3 dark:border-white/10">
              {attributionCapturedAt && (
                <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
                  Captured when the buyer chose to pay with cryptocurrency
                  {(() => {
                    const t = new Date(attributionCapturedAt);
                    return Number.isNaN(t.getTime()) ? '' : ` on ${t.toLocaleDateString()}`;
                  })()}
                  {attributionProduct ? ` (requested ${attributionProduct})` : ''}. Edit or clear
                  before creating the order.
                </p>
              )}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {ATTRIBUTION_FIELDS.map(({ key, label, placeholder }) => (
                  <Field key={key} label={label}>
                    <input
                      type="text"
                      value={attribution[key] ?? ''}
                      onChange={(e) => setAttributionField(key, e.target.value)}
                      placeholder={placeholder ?? '—'}
                      className={inputCls}
                      autoComplete="off"
                    />
                  </Field>
                ))}
              </div>
            </div>
          </details>
        </DialogBody>
        <DialogFooter>
          <DialogCloseButton variant="outline">Cancel</DialogCloseButton>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-zinc-900 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-zinc-700 disabled:opacity-50 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
          >
            {submitting ? 'Creating…' : 'Create Order'}
          </button>
        </DialogFooter>
      </form>
    </>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
        {required && <span className="mr-0.5 text-red-500">*</span>}
        {label}
      </label>
      {children}
    </div>
  );
}

function CheckboxField({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900 dark:border-zinc-600 dark:text-white"
      />
      <div>
        <div className="text-sm font-medium text-zinc-900 dark:text-white">{label}</div>
        {hint && <div className="text-xs text-zinc-500 dark:text-zinc-400">{hint}</div>}
      </div>
    </label>
  );
}

const inputCls =
  'h-9 w-full rounded-md border border-zinc-950/10 bg-white px-3 text-sm text-zinc-950 placeholder-zinc-400 outline-none transition focus:border-zinc-950/20 focus:ring-2 focus:ring-zinc-950/10 dark:border-white/10 dark:bg-zinc-900 dark:text-white dark:placeholder-zinc-500 dark:focus:border-white/20';
